import React from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Download } from "lucide-react";
import Shell from "@/components/Shell";
import SafetyLink from "@/components/SafetyLink";
import { useApp } from "@/lib/store";

export default function Landing() {
  const nav = useNavigate();
  const { canInstall, promptInstall } = useApp();
  const reduce = useReducedMotion();
  const rise = reduce ? 0 : 14;

  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: 0.12, delayChildren: 0.08 },
    },
  };
  const item = {
    hidden: { opacity: 0, y: rise },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <Shell>
      <div className="min-h-screen flex flex-col px-6 pt-10 pb-10">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="flex-1 flex flex-col justify-center"
        >
          <motion.span
            variants={item}
            className="text-xs font-semibold uppercase tracking-[0.2em] text-[#6B6B6B]"
          >
            Signal State ID
          </motion.span>

          <div className="mt-10">
            <h1 className="font-serif-display text-5xl sm:text-6xl leading-[1.05] text-[#1A1A1A]">
              <motion.span variants={item} className="block">Something</motion.span>
              <motion.span variants={item} className="block">feels off.</motion.span>
            </h1>
            <motion.p variants={item} className="mt-5 text-2xl font-serif-display text-[#474747]">
              You don't need the words for it yet.
            </motion.p>
            <motion.p variants={item} className="mt-6 text-[15px] leading-relaxed text-[#474747] max-w-sm">
              Three quick taps — how it feels, what you need to do next, and how much space you have.
              Signal State ID gives you one realistic thing to do. No diagnosis, no fixing everything.
            </motion.p>
          </div>

          <div className="mt-10 space-y-4">
            <motion.button
              variants={item}
              data-testid="find-move-btn"
              onClick={() => nav("/navigate")}
              className="w-full bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5"
            >
              Find my next move
            </motion.button>
            <motion.button
              variants={item}
              data-testid="baseline-link"
              onClick={() => nav("/navigate?baseline=1")}
              className="w-full text-center text-[15px] text-[#474747] hover:text-[#1A1A1A] ss-focus transition-colors py-2"
            >
              I feel steady and want to record a baseline
            </motion.button>
          </div>

          <motion.div variants={item} className="mt-10 flex flex-col items-start gap-4">
            <p className="text-[13px] text-[#6B6B6B]">
              Private. Nonclinical. No judgment. No streaks.
            </p>
            {canInstall && (
              <button
                data-testid="install-app"
                onClick={promptInstall}
                className="inline-flex items-center gap-2 text-[13px] text-[#8a6d10] hover:text-[#1A1A1A] ss-focus transition-colors"
              >
                <Download size={15} strokeWidth={1.5} /> Add Signal State ID to your home screen
              </button>
            )}
            <SafetyLink />
          </motion.div>
        </motion.div>
      </div>
    </Shell>
  );
}
